let statesData = null;

export default {
  methods: {
    async getStateNameFromCode(shortCode) {
      if (!shortCode) return null;
      if (!statesData) {
        const resp = await this.$axios.get("states", { params: { country_code: "US" } });
        statesData = resp.data;
      }
      return statesData.find((s) => s.short_code === shortCode)?.name || shortCode;
    },

    /**
     * Build profile props from a user record (if available).
     *
     * Single definition shared by the page-view path (updateActions below) and
     * the profile-save path (account.vue). Two copies would drift silently the
     * moment a field is added — nothing errors, HubSpot just keeps the stale
     * value on one of the two paths.
     *
     * Pass a user to build from a specific record (e.g. the one the API just
     * saved); omit it to read the logged-in user from the store.
     */
    async buildHubSpotIdentifyPayload(user) {
      const identifyPayload = {};
      const u = user ?? this.currentUser?.user;
      if (u) {
        if (u.email)        identifyPayload.email                = u.email;
        if (u.firstname)    identifyPayload.firstname            = u.firstname;
        if (u.lastname)     identifyPayload.lastname             = u.lastname;
        if (u.title)        identifyPayload.jobtitle             = u.title;
        if (u.organization) identifyPayload.company              = u.organization;
        if (u.country)      identifyPayload.country__dropdown_   = u.country;
        if (u.state)        identifyPayload.state_htb            = await this.getStateNameFromCode(u.state);
      }
      return identifyPayload;
    },

    /**
     * Push the profile props to HubSpot immediately.
     *
     * Use after a profile save, where waiting for the next updateActions() call
     * is not good enough: that only runs on mount, so an edit would sit unsent
     * until the visitor navigated somewhere tracked — and be lost entirely if
     * they closed the tab after saving.
     *
     * The trailing trackPageView is load-bearing. `identify` only stages the
     * properties in the _hsq queue; HubSpot attaches them to the contact on the
     * next tracked interaction, so without a flush nothing is actually sent.
     *
     * Returns true when something was pushed, false when skipped (non-production
     * build, no window, or no email to attach the properties to).
     */
    async identifyProfileWithHubSpot(user, pagelink) {
      if (process.env.VUE_APP_PLATFORM !== "production") return false;
      if (typeof window === "undefined") return false;

      const u = user ?? this.currentUser?.user;
      if (!u?.email) return false;

      const identifyPayload = await this.buildHubSpotIdentifyPayload(u);

      window._hsq = window._hsq || [];
      window._hsq.push(["identify", identifyPayload]);
      window._hsq.push(["setPath", pagelink ?? window.location.pathname]);
      window._hsq.push(["setTitle", document.title]);
      window._hsq.push(["trackPageView"]);

      return true;
    },

    async updateActions(pagelink) {
      let actionJson = {
        pagelink: pagelink,
      };

      // Build profile props from logged-in user fields (if available)
      const identifyPayload = await this.buildHubSpotIdentifyPayload();

      if (this.currentUser) {
        actionJson.userId  = this.currentUser?.user?.id;
        actionJson.email   = this.currentUser.user.email;
        actionJson.sessionId = this.currentUser.sessionId;
      }

      const platform = process.env.VUE_APP_PLATFORM;

      this.$axios.post("action-update", actionJson).then(() => {

        if (platform === "production" && typeof window !== "undefined") {
          
          // Track event in HubSpot
          window._hsq = window._hsq || [];
          if (this.currentUser && actionJson.email) {
            window._hsq.push([
              "identify",
              identifyPayload,
            ]);
          }
          window._hsq.push(["setPath", actionJson.pagelink]);
          window._hsq.push(["setTitle", document.title]);
          window._hsq.push(["trackPageView"]);
        }
      });
    },
  },
};
